import React, { useEffect, useMemo, useState } from 'react';
import { Action, OrganizationEntityCategory } from '@anodot-cost/rbac-client';
import { useRootStore } from 'app/contexts/RootStoreContext';
import { useBrand } from 'app/contexts/Brand/BrandContext';
import { usePermissions } from 'app/contexts/PermissionsContext';
import classNames from 'classnames';
import InfoPopover from 'shared/components/andtComponents/InfoPopover/index';
import { observer } from 'mobx-react';

import styles from './PricingModeSwitch.module.scss';

const PRICING_MODE_OPTIONS = {
  CUSTOMER: {
    label: 'Customer',
  },
  PARTNER: {
    label: 'Partner',
  },
};

const PricingModeSwitch = () => {
  const { appStore, usersStore } = useRootStore();
  const brand = useBrand();
  const { isHasPermission, permissionsLoading } = usePermissions();
  const isPpAppliedLocal = appStore.getIsPpApplied(usersStore);

  const [isCustomersView, setIsCustomersView] = useState(isPpAppliedLocal);

  useEffect(() => {
    setIsCustomersView(isPpAppliedLocal);
  }, [isPpAppliedLocal]);

  const options = useMemo(
    () => [
      {
        isSelected: isCustomersView === true,
        label: PRICING_MODE_OPTIONS.CUSTOMER.label,
      },
      {
        isSelected: isCustomersView === false,
        label: PRICING_MODE_OPTIONS.PARTNER.label,
      },
    ],
    [isCustomersView],
  );

  const itemClickedHandler = (option) => {
    if (partnerModeAvailable) {
      if (option.label === PRICING_MODE_OPTIONS.CUSTOMER.label) {
        if (!appStore.isPpApplied) {
          appStore.updatePricingMode(true);
        }
      } else if (option.label === PRICING_MODE_OPTIONS.PARTNER.label) {
        if (appStore.isPpApplied) {
          appStore.updatePricingMode(false);
        }
      }
    }
  };

  const handleKeyDown = (e, option) => {
    if (partnerModeAvailable) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        itemClickedHandler(option);
      }
    }
  };

  const partnerModeAvailable = appStore.isKeyCloakManagement
    ? isHasPermission(OrganizationEntityCategory.ResellerUsersPreRebillingDataAccess, Action.View)
    : true;

  useEffect(() => {
    if (!partnerModeAvailable && !permissionsLoading) {
      appStore.updatePricingMode(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStore, partnerModeAvailable]);

  const toolTipTitle = (
    <div>
      Select which prices you would like to see:
      <br />
      1) Partner prices before the re-billing.
      <br />
      2) Billing Customer prices after the re-billing process.
      <br />
      <a href={brand.externalLinks.DocumentationForResellers} target="_blank" rel="noopener noreferrer">
        Click here for more details on the impacted capabilities.
      </a>
    </div>
  );

  return permissionsLoading ? null : (
    <div className={styles.pricingModeContainer}>
      <div className={styles.pricingModeLabelContainer}>
        Pricing mode:
        <InfoPopover placement="top" isSimple>
          {toolTipTitle}
        </InfoPopover>
      </div>

      <div className={styles.switchOptionsContainer} role="radiogroup" aria-label="Pricing mode options">
        {options.map((option) => (
          <label
            key={option.label}
            className={classNames(
              styles.option,
              { [styles.selected]: option.isSelected },
              { [styles.disabled]: !partnerModeAvailable && option.label === PRICING_MODE_OPTIONS.PARTNER.label },
            )}
          >
            <input
              type="radio"
              name="pricingMode"
              value={option.label}
              checked={option.isSelected}
              disabled={!partnerModeAvailable && option.label === PRICING_MODE_OPTIONS.PARTNER.label}
              onChange={() => itemClickedHandler(option)}
              onKeyDown={(e) => handleKeyDown(e, option)}
              tabIndex={!option.isSelected ? 0 : -1}
              aria-checked={option.isSelected}
            />
            {option.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const ObserverPricingModeSwitch = observer(PricingModeSwitch);
export default ObserverPricingModeSwitch;
